import { switchTab } from './app.controller.js';
import { state } from '../state.js';
import { Connection } from '../types.js';
import { escapeHtml, formatAuthType } from '../utils/formatters.js';
import { showToast } from '../utils/toast.js';

/**
 * Fetch all connections from backend and update both summary & detailed views
 */
export async function fetchConnections(): Promise<void> {
  try {
    const res = await fetch('/api/v1/connections');
    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    const data = await res.json();
    const connections: Connection[] = data.connections || [];

    state.connections.list = connections;
    state.connections.total = connections.length;

    const activeSource = connections.find((c) => c.type === 'source' && Boolean(c.is_active));
    const activeTarget = connections.find((c) => c.type === 'target' && Boolean(c.is_active));

    state.connections.source = activeSource || null;
    state.connections.target = activeTarget || null;

    updateHeaderAndOverviewMetrics(connections, activeSource || null, activeTarget || null);
    renderActivePair(activeSource || null, activeTarget || null);
    renderConnectionsList();
  } catch (err) {
    console.warn('Failed to load connections:', err);
  }
}

/**
 * Update Top Navbar and Overview Tab KPI metrics
 */
export function updateHeaderAndOverviewMetrics(
  connections: Connection[],
  activeSource: Connection | null,
  activeTarget: Connection | null
): void {
  const tabBadge = document.getElementById('tab-connections-count');
  const metricSource = document.getElementById('metric-source-conn');
  const metricTarget = document.getElementById('metric-target-conn');
  const metricTotal = document.getElementById('metric-total-conns');
  const sourceName = document.getElementById('source-name');
  const targetName = document.getElementById('target-name');
  const sourceDot = document.getElementById('source-dot');
  const targetDot = document.getElementById('target-dot');
  const badgeConnections = document.getElementById('badge-connections-status');

  // Filter count tabs
  const sourceCount = connections.filter((c) => c.type === 'source').length;
  const targetCount = connections.filter((c) => c.type === 'target').length;

  const countAll = document.getElementById('filter-count-all');
  const countSource = document.getElementById('filter-count-source');
  const countTarget = document.getElementById('filter-count-target');

  if (countAll) countAll.textContent = String(connections.length);
  if (countSource) countSource.textContent = String(sourceCount);
  if (countTarget) countTarget.textContent = String(targetCount);

  // Header badges
  if (tabBadge) tabBadge.textContent = String(connections.length);
  if (metricTotal) metricTotal.textContent = `${connections.length} configured`;

  // Source display
  if (activeSource) {
    if (sourceName) sourceName.textContent = activeSource.name;
    if (sourceDot) sourceDot.classList.add('active');
    if (metricSource) metricSource.textContent = `${activeSource.name} (${activeSource.url})`;
  } else {
    if (sourceName) sourceName.textContent = 'None';
    if (sourceDot) sourceDot.classList.remove('active');
    if (metricSource) metricSource.textContent = 'None active';
  }

  // Target display
  if (activeTarget) {
    if (targetName) targetName.textContent = activeTarget.name;
    if (targetDot) targetDot.classList.add('active');
    if (metricTarget) metricTarget.textContent = `${activeTarget.name} (${activeTarget.url})`;
  } else {
    if (targetName) targetName.textContent = 'None';
    if (targetDot) targetDot.classList.remove('active');
    if (metricTarget) metricTarget.textContent = 'None active';
  }

  if (badgeConnections) {
    if (activeSource && activeTarget) {
      badgeConnections.textContent = 'Connected';
      badgeConnections.className = 'badge badge-success';
    } else if (activeSource || activeTarget) {
      badgeConnections.textContent = 'Partial';
      badgeConnections.className = 'badge badge-warning';
    } else {
      badgeConnections.textContent = 'Unconfigured';
      badgeConnections.className = 'badge badge-neutral';
    }
  }
}

/**
 * Render the Active Pipeline Pair Card (Source -> Sync -> Target)
 */
export function renderActivePair(activeSource: Connection | null, activeTarget: Connection | null): void {
  // Source Box
  const boxSourceName = document.getElementById('box-source-name');
  const boxSourceUrl = document.getElementById('box-source-url');
  const boxSourceAuth = document.getElementById('box-source-auth');
  const boxSourceStatus = document.getElementById('box-source-status');
  const boxSourceBadge = document.getElementById('box-source-badge');
  const boxSourceDot = document.getElementById('box-source-dot');
  const btnTestSource = document.getElementById('btn-test-active-source') as HTMLButtonElement | null;
  const btnEditSource = document.getElementById('btn-edit-active-source') as HTMLButtonElement | null;

  if (activeSource) {
    if (boxSourceName) boxSourceName.textContent = activeSource.name;
    if (boxSourceUrl) boxSourceUrl.textContent = activeSource.url;
    if (boxSourceAuth) boxSourceAuth.textContent = formatAuthType(activeSource.auth_type);
    if (boxSourceBadge) {
      boxSourceBadge.textContent = 'Active Source';
      boxSourceBadge.className = 'badge badge-success';
    }
    if (boxSourceDot) boxSourceDot.classList.add('active');
    if (btnTestSource) {
      btnTestSource.disabled = false;
      btnTestSource.onclick = () => testConnectionById(activeSource.id, btnTestSource);
    }
    if (btnEditSource) {
      btnEditSource.disabled = false;
      btnEditSource.onclick = () => editConnection(activeSource.id);
    }

    const testRes = state.connections.testResults[activeSource.id];
    if (boxSourceStatus) {
      boxSourceStatus.textContent = testRes
        ? testRes.success
          ? `Online (${testRes.latencyMs}ms)`
          : `Error (HTTP ${testRes.statusCode || '500'})`
        : 'Active';
    }
  } else {
    if (boxSourceName) boxSourceName.textContent = 'No Active Source';
    if (boxSourceUrl) boxSourceUrl.textContent = 'Not configured';
    if (boxSourceAuth) boxSourceAuth.textContent = 'None';
    if (boxSourceStatus) boxSourceStatus.textContent = 'Idle';
    if (boxSourceBadge) {
      boxSourceBadge.textContent = 'Unset';
      boxSourceBadge.className = 'badge badge-neutral';
    }
    if (boxSourceDot) boxSourceDot.classList.remove('active');
    if (btnTestSource) btnTestSource.disabled = true;
    if (btnEditSource) btnEditSource.disabled = true;
  }

  // Target Box
  const boxTargetName = document.getElementById('box-target-name');
  const boxTargetUrl = document.getElementById('box-target-url');
  const boxTargetAuth = document.getElementById('box-target-auth');
  const boxTargetStatus = document.getElementById('box-target-status');
  const boxTargetBadge = document.getElementById('box-target-badge');
  const boxTargetDot = document.getElementById('box-target-dot');
  const btnTestTarget = document.getElementById('btn-test-active-target') as HTMLButtonElement | null;
  const btnEditTarget = document.getElementById('btn-edit-active-target') as HTMLButtonElement | null;

  if (activeTarget) {
    if (boxTargetName) boxTargetName.textContent = activeTarget.name;
    if (boxTargetUrl) boxTargetUrl.textContent = activeTarget.url;
    if (boxTargetAuth) boxTargetAuth.textContent = formatAuthType(activeTarget.auth_type);
    if (boxTargetBadge) {
      boxTargetBadge.textContent = 'Active Target';
      boxTargetBadge.className = 'badge badge-success';
    }
    if (boxTargetDot) boxTargetDot.classList.add('active');
    if (btnTestTarget) {
      btnTestTarget.disabled = false;
      btnTestTarget.onclick = () => testConnectionById(activeTarget.id, btnTestTarget);
    }
    if (btnEditTarget) {
      btnEditTarget.disabled = false;
      btnEditTarget.onclick = () => editConnection(activeTarget.id);
    }

    const testRes = state.connections.testResults[activeTarget.id];
    if (boxTargetStatus) {
      boxTargetStatus.textContent = testRes
        ? testRes.success
          ? `Online (${testRes.latencyMs}ms)`
          : `Error (HTTP ${testRes.statusCode || '500'})`
        : 'Active';
    }
  } else {
    if (boxTargetName) boxTargetName.textContent = 'No Active Target';
    if (boxTargetUrl) boxTargetUrl.textContent = 'Not configured';
    if (boxTargetAuth) boxTargetAuth.textContent = 'None';
    if (boxTargetStatus) boxTargetStatus.textContent = 'Idle';
    if (boxTargetBadge) {
      boxTargetBadge.textContent = 'Unset';
      boxTargetBadge.className = 'badge badge-neutral';
    }
    if (boxTargetDot) boxTargetDot.classList.remove('active');
    if (btnTestTarget) btnTestTarget.disabled = true;
    if (btnEditTarget) btnEditTarget.disabled = true;
  }
}

/**
 * Render the Inventory List of all connections matching filters
 */
export function renderConnectionsList(): void {
  const container = document.getElementById('connections-list-grid');
  const statsText = document.getElementById('inventory-stats-text');
  if (!container) return;

  const { list, filter, search } = state.connections;

  let filtered = list;

  // Filter by Type
  if (filter === 'source') {
    filtered = filtered.filter((c) => c.type === 'source');
  } else if (filter === 'target') {
    filtered = filtered.filter((c) => c.type === 'target');
  }

  // Filter by Search Query
  if (search.trim()) {
    const q = search.toLowerCase().trim();
    filtered = filtered.filter(
      (c) =>
        (c.name && c.name.toLowerCase().includes(q)) ||
        (c.url && c.url.toLowerCase().includes(q)) ||
        (c.client_id && c.client_id.toLowerCase().includes(q)) ||
        (c.id && c.id.toLowerCase().includes(q))
    );
  }

  if (statsText) {
    statsText.textContent = `Showing ${filtered.length} of ${list.length} connections`;
  }

  if (filtered.length === 0) {
    container.innerHTML = `
      <div class="card empty-placeholder-card" style="grid-column: 1 / -1; margin: 1rem auto; width: 100%;">
        <div class="placeholder-icon">🔗</div>
        <h3>No Connections Found</h3>
        <p>${
          list.length === 0
            ? 'No Akeneo PIM connections configured yet. Use the form above to add your Source and Target instances.'
            : 'No connections match your current search and filter criteria.'
        }</p>
        <button class="btn btn-primary" onclick="window.AkeneoDashboard.scrollToForm()">
          ➕ Add New Connection
        </button>
      </div>
    `;
    return;
  }

  container.innerHTML = filtered
    .map((conn) => {
      const isSource = conn.type === 'source';
      const roleClass = isSource ? 'conn-role-source' : 'conn-role-target';
      const roleLabel = isSource ? 'Source (Extract)' : 'Target (Load)';
      const isActive = Boolean(conn.is_active);
      const testResult = state.connections.testResults[conn.id];

      let testStatusHtml = '';
      if (testResult) {
        if (testResult.success) {
          testStatusHtml = `
            <div class="conn-card-test-status" style="background-color: var(--status-success-bg); color: var(--status-success);">
              <span>✓ 200 OK (${testResult.latencyMs}ms)</span>
              <span class="mono" style="font-size: 0.7rem;">Verified</span>
            </div>
          `;
        } else {
          testStatusHtml = `
            <div class="conn-card-test-status" style="background-color: var(--status-danger-bg); color: var(--status-danger);">
              <span>✕ Failed (HTTP ${testResult.statusCode || 500})</span>
              <span class="mono" style="font-size: 0.7rem;">${escapeHtml(testResult.message).substring(0, 30)}</span>
            </div>
          `;
        }
      }

      return `
        <div class="connection-item-card ${isActive ? 'is-active-card' : ''}" id="conn-card-${escapeHtml(conn.id)}">
          <div class="conn-card-header">
            <div class="conn-badges-group">
              <span class="conn-role-pill ${roleClass}">${roleLabel}</span>
              ${
                isActive
                  ? `<span class="badge badge-success">Active ${isSource ? 'Source' : 'Target'}</span>`
                  : `<span class="badge badge-neutral">Standby</span>`
              }
            </div>
            <span class="mono" style="font-size: 0.75rem; color: var(--text-muted);">${escapeHtml(conn.id)}</span>
          </div>

          <div class="conn-card-body">
            <div class="conn-card-title">${escapeHtml(conn.name)}</div>
            <div class="conn-card-url mono">
              <span>🌐</span>
              <a href="${escapeHtml(conn.url)}" target="_blank" rel="noopener noreferrer">${escapeHtml(conn.url)}</a>
            </div>

            <div class="conn-card-meta-grid">
              <div class="conn-meta-row">
                <span class="conn-meta-label">Auth Method</span>
                <span class="conn-meta-value">${escapeHtml(formatAuthType(conn.auth_type))}</span>
              </div>
              <div class="conn-meta-row">
                <span class="conn-meta-label">Client ID</span>
                <span class="conn-meta-value mono">${escapeHtml(conn.client_id || '-')}</span>
              </div>
              <div class="conn-meta-row">
                <span class="conn-meta-label">Secret</span>
                <span class="conn-meta-value mono">${escapeHtml(conn.client_secret_masked || '••••••••')}</span>
              </div>
              <div class="conn-meta-row">
                <span class="conn-meta-label">User / Token</span>
                <span class="conn-meta-value">${escapeHtml(conn.username || (conn.has_access_token ? 'Active Token' : '-'))}</span>
              </div>
            </div>

            ${testStatusHtml}
          </div>

          <div class="conn-card-footer">
            <div class="conn-actions-left">
              <button type="button" class="btn btn-sm btn-secondary btn-conn-test" data-action="test" data-id="${escapeHtml(conn.id)}">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
                <span>Test Link</span>
              </button>
              ${
                !isActive
                  ? `<button type="button" class="btn btn-sm btn-outline btn-conn-activate" data-action="activate" data-id="${escapeHtml(conn.id)}">
                      <span>Set Active</span>
                    </button>`
                  : ''
              }
            </div>

            <div class="conn-actions-right">
              <button type="button" class="btn btn-sm btn-secondary btn-conn-edit" data-action="edit" data-id="${escapeHtml(conn.id)}">
                <span>Edit</span>
              </button>
              <button type="button" class="btn btn-sm btn-danger-outline btn-conn-delete" data-action="delete" data-id="${escapeHtml(conn.id)}" data-name="${escapeHtml(conn.name)}">
                <span>Delete</span>
              </button>
            </div>
          </div>
        </div>
      `;
    })
    .join('');
}

/**
 * Handle changing auth_type dropdown to show/hide relevant credential fields
 */
export function handleAuthTypeChange(): void {
  const authTypeSelect = document.getElementById('conn-auth-type') as HTMLSelectElement | null;
  if (!authTypeSelect) return;

  const authType = authTypeSelect.value;
  const groupUsername = document.getElementById('group-conn-username');
  const groupPassword = document.getElementById('group-conn-password');
  const groupAccessToken = document.getElementById('group-conn-access-token');
  const groupRefreshToken = document.getElementById('group-conn-refresh-token');
  const secretStar = document.getElementById('secret-required-star');
  const secretInput = document.getElementById('conn-client-secret') as HTMLInputElement | null;
  const secretHint = document.getElementById('secret-hint');

  const isEditing = Boolean(state.connections.editingId);

  if (authType === 'oauth_password') {
    if (groupUsername) groupUsername.style.display = 'flex';
    if (groupPassword) groupPassword.style.display = 'flex';
    if (groupAccessToken) groupAccessToken.style.display = 'none';
    if (groupRefreshToken) groupRefreshToken.style.display = 'none';

    if (secretStar) secretStar.style.display = isEditing ? 'none' : 'inline';
    if (secretInput && !isEditing) {
      secretInput.placeholder = 'Enter client secret';
    }
    if (secretHint && !isEditing) {
      secretHint.textContent = 'Encrypted using AES-256-GCM at rest';
    }
  } else if (authType === 'oauth_token') {
    if (groupUsername) groupUsername.style.display = 'none';
    if (groupPassword) groupPassword.style.display = 'none';
    if (groupAccessToken) groupAccessToken.style.display = 'flex';
    if (groupRefreshToken) groupRefreshToken.style.display = 'flex';

    if (secretStar) secretStar.style.display = isEditing ? 'none' : 'inline';
    if (secretInput && !isEditing) {
      secretInput.placeholder = 'Enter client secret';
    }
    if (secretHint && !isEditing) {
      secretHint.textContent = 'Encrypted using AES-256-GCM at rest';
    }
  } else if (authType === 'app_token') {
    if (groupUsername) groupUsername.style.display = 'none';
    if (groupPassword) groupPassword.style.display = 'none';
    if (groupAccessToken) groupAccessToken.style.display = 'flex';
    if (groupRefreshToken) groupRefreshToken.style.display = 'none';

    if (secretStar) secretStar.style.display = 'none';
    if (secretInput && !isEditing) {
      secretInput.placeholder = 'Enter client secret (optional if access token provided)';
    }
    if (secretHint && !isEditing) {
      secretHint.textContent = 'Optional if direct Bearer access token is provided';
    }
  }
}

/**
 * Populate Connection Form for editing existing connection
 */
export function editConnection(id: string): void {
  switchTab('connections');
  const conn = state.connections.list.find((c) => c.id === id);
  if (!conn) {
    showToast('Connection not found in cache', 'error');
    return;
  }

  state.connections.editingId = id;

  const formCard = document.getElementById('connection-form-card');
  const formTitle = document.getElementById('form-card-title');
  const formSubtitle = document.getElementById('form-card-subtitle');
  const formBadge = document.getElementById('form-mode-badge');
  const saveBtnText = document.getElementById('save-btn-text');
  const formModeIcon = document.getElementById('form-mode-icon');
  const secretHint = document.getElementById('secret-hint');
  const secretStar = document.getElementById('secret-required-star');

  if (formTitle) formTitle.textContent = `Edit Connection: ${conn.name}`;
  if (formSubtitle) formSubtitle.textContent = `Update credentials and settings for ID: ${conn.id}`;
  if (formBadge) {
    formBadge.textContent = 'Editing Profile';
    formBadge.className = 'badge badge-warning';
  }
  if (saveBtnText) saveBtnText.textContent = 'Update Connection';
  if (formModeIcon) formModeIcon.textContent = '✏️';
  if (secretHint) secretHint.textContent = 'Leave empty to retain existing stored encrypted secret';
  if (secretStar) secretStar.style.display = 'none';

  // Fill Inputs
  const inputId = document.getElementById('conn-id') as HTMLInputElement | null;
  const inputName = document.getElementById('conn-name') as HTMLInputElement | null;
  const inputUrl = document.getElementById('conn-url') as HTMLInputElement | null;
  const inputClientId = document.getElementById('conn-client-id') as HTMLInputElement | null;
  const inputClientSecret = document.getElementById('conn-client-secret') as HTMLInputElement | null;
  const inputUsername = document.getElementById('conn-username') as HTMLInputElement | null;
  const inputPassword = document.getElementById('conn-password') as HTMLInputElement | null;
  const inputAccessToken = document.getElementById('conn-access-token') as HTMLInputElement | null;
  const inputRefreshToken = document.getElementById('conn-refresh-token') as HTMLInputElement | null;
  const inputIsActive = document.getElementById('conn-is-active') as HTMLInputElement | null;
  const selectAuthType = document.getElementById('conn-auth-type') as HTMLSelectElement | null;

  if (inputId) inputId.value = conn.id;
  if (inputName) inputName.value = conn.name;
  if (inputUrl) inputUrl.value = (conn.url || '').replace(/^https?:\/\//, '');
  if (inputClientId) inputClientId.value = conn.client_id || '';
  if (inputClientSecret) {
    inputClientSecret.value = '';
    inputClientSecret.placeholder = '•••••••• (Encrypted Secret Preserved)';
  }
  if (inputUsername) inputUsername.value = conn.username || '';
  if (inputPassword) {
    inputPassword.value = '';
    inputPassword.placeholder = conn.has_password ? '•••••••• (Password Preserved)' : '';
  }
  if (inputAccessToken) inputAccessToken.value = '';
  if (inputRefreshToken) inputRefreshToken.value = '';
  if (inputIsActive) inputIsActive.checked = Boolean(conn.is_active);

  // Type Radio
  const radioSource = document.getElementById('conn-type-source') as HTMLInputElement | null;
  const radioTarget = document.getElementById('conn-type-target') as HTMLInputElement | null;
  if (conn.type === 'target') {
    if (radioTarget) radioTarget.checked = true;
  } else {
    if (radioSource) radioSource.checked = true;
  }

  // Auth Type
  if (selectAuthType) {
    selectAuthType.value = conn.auth_type || 'oauth_password';
    handleAuthTypeChange();
  }

  // Extra config
  if (conn.extra_config && typeof conn.extra_config === 'object') {
    const inputBatch = document.getElementById('conn-batch-size') as HTMLInputElement | null;
    const inputTimeout = document.getElementById('conn-timeout') as HTMLInputElement | null;
    const inputHeaders = document.getElementById('conn-custom-headers') as HTMLTextAreaElement | null;
    if (inputBatch && conn.extra_config.batch_size) inputBatch.value = String(conn.extra_config.batch_size);
    if (inputTimeout && conn.extra_config.timeout) inputTimeout.value = String(conn.extra_config.timeout);
    if (inputHeaders && conn.extra_config.custom_headers) {
      inputHeaders.value = JSON.stringify(conn.extra_config.custom_headers, null, 2);
    }
  }

  // Scroll to form
  if (formCard) {
    formCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (inputName) inputName.focus();
  }
}

/**
 * Reset Connection Form to clean "Create New" state
 */
export function resetConnectionForm(): void {
  state.connections.editingId = null;

  const form = document.getElementById('connection-form') as HTMLFormElement | null;
  if (form) form.reset();

  const inputId = document.getElementById('conn-id') as HTMLInputElement | null;
  if (inputId) inputId.value = '';

  const inputSecret = document.getElementById('conn-client-secret') as HTMLInputElement | null;
  if (inputSecret) inputSecret.placeholder = 'Enter client secret';

  const inputPassword = document.getElementById('conn-password') as HTMLInputElement | null;
  if (inputPassword) inputPassword.placeholder = 'Enter password';

  const formTitle = document.getElementById('form-card-title');
  const formSubtitle = document.getElementById('form-card-subtitle');
  const formBadge = document.getElementById('form-mode-badge');
  const saveBtnText = document.getElementById('save-btn-text');
  const formModeIcon = document.getElementById('form-mode-icon');
  const secretHint = document.getElementById('secret-hint');
  const secretStar = document.getElementById('secret-required-star');
  const testBox = document.getElementById('form-test-result');

  if (formTitle) formTitle.textContent = 'Configure Connection';
  if (formSubtitle) formSubtitle.textContent = 'Create a new Akeneo instance credential profile';
  if (formBadge) {
    formBadge.textContent = 'New Connection';
    formBadge.className = 'badge badge-accent';
  }
  if (saveBtnText) saveBtnText.textContent = 'Save Connection';
  if (formModeIcon) formModeIcon.textContent = '➕';
  if (secretHint) secretHint.textContent = 'Encrypted using AES-256-GCM at rest';
  if (secretStar) secretStar.style.display = 'inline';
  if (testBox) testBox.style.display = 'none';

  const radioSource = document.getElementById('conn-type-source') as HTMLInputElement | null;
  if (radioSource) radioSource.checked = true;

  const selectAuthType = document.getElementById('conn-auth-type') as HTMLSelectElement | null;
  if (selectAuthType) {
    selectAuthType.value = 'oauth_password';
    handleAuthTypeChange();
  }
}

/**
 * Collect and validate form inputs
 */
export function collectFormPayload(): Record<string, any> {
  const inputId = document.getElementById('conn-id') as HTMLInputElement | null;
  const inputName = document.getElementById('conn-name') as HTMLInputElement | null;
  const inputUrl = document.getElementById('conn-url') as HTMLInputElement | null;
  const inputClientId = document.getElementById('conn-client-id') as HTMLInputElement | null;
  const inputClientSecret = document.getElementById('conn-client-secret') as HTMLInputElement | null;
  const inputUsername = document.getElementById('conn-username') as HTMLInputElement | null;
  const inputPassword = document.getElementById('conn-password') as HTMLInputElement | null;
  const inputAccessToken = document.getElementById('conn-access-token') as HTMLInputElement | null;
  const inputRefreshToken = document.getElementById('conn-refresh-token') as HTMLInputElement | null;
  const inputIsActive = document.getElementById('conn-is-active') as HTMLInputElement | null;
  const selectAuthType = document.getElementById('conn-auth-type') as HTMLSelectElement | null;
  const radioTarget = document.getElementById('conn-type-target') as HTMLInputElement | null;

  const name = (inputName?.value || '').trim();
  const rawUrl = (inputUrl?.value || '').trim();
  const clientId = (inputClientId?.value || '').trim();
  const clientSecret = inputClientSecret?.value || '';
  const authType = selectAuthType?.value || 'oauth_password';
  const type = radioTarget?.checked ? 'target' : 'source';
  const isActive = inputIsActive ? inputIsActive.checked : true;
  const username = inputUsername ? inputUsername.value.trim() : '';
  const password = inputPassword ? inputPassword.value : '';
  const accessToken = inputAccessToken ? inputAccessToken.value.trim() : '';
  const refreshToken = inputRefreshToken ? inputRefreshToken.value.trim() : '';

  if (!name) {
    throw new Error('Connection Name is required');
  }
  if (!rawUrl) {
    throw new Error('Akeneo Instance URL is required');
  }
  if (!clientId) {
    throw new Error('Client ID is required');
  }

  // Normalize URL
  let url = rawUrl;
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    url = `https://${url}`;
  }

  // Advanced extra_config
  const extraConfig: Record<string, any> = {};
  const batchSize = (document.getElementById('conn-batch-size') as HTMLInputElement | null)?.value;
  const timeout = (document.getElementById('conn-timeout') as HTMLInputElement | null)?.value;
  const customHeaders = (document.getElementById('conn-custom-headers') as HTMLTextAreaElement | null)?.value;

  if (batchSize) extraConfig.batch_size = parseInt(batchSize, 10);
  if (timeout) extraConfig.timeout = parseInt(timeout, 10);
  if (customHeaders && customHeaders.trim()) {
    try {
      extraConfig.custom_headers = JSON.parse(customHeaders.trim());
    } catch {
      throw new Error('Custom Headers must be valid JSON');
    }
  }

  const payload: Record<string, any> = {
    name,
    type,
    url,
    client_id: clientId,
    auth_type: authType,
    is_active: isActive,
    extra_config: Object.keys(extraConfig).length > 0 ? extraConfig : null,
  };

  if (inputId && inputId.value) {
    payload.id = inputId.value;
  }
  if (clientSecret) {
    payload.client_secret = clientSecret;
  }
  if (username) {
    payload.username = username;
  }
  if (password) {
    payload.password = password;
  }
  if (accessToken) {
    payload.access_token = accessToken;
  }
  if (refreshToken) {
    payload.refresh_token = refreshToken;
  }

  return payload;
}

/**
 * Test Connection using current form values (before saving)
 */
export async function testConnectionForm(): Promise<void> {
  const testBtn = document.getElementById('btn-test-form-conn') as HTMLButtonElement | null;
  const testBtnText = document.getElementById('test-btn-text');
  const testBox = document.getElementById('form-test-result');
  const testBadge = document.getElementById('test-result-badge');
  const testLatency = document.getElementById('test-result-latency');
  const testMessage = document.getElementById('test-result-message');
  const testDetails = document.getElementById('test-result-details');

  let payload: Record<string, any>;
  try {
    payload = collectFormPayload();
  } catch (err: any) {
    showToast(err.message, 'error');
    return;
  }

  if (testBtn) testBtn.disabled = true;
  if (testBtnText) testBtnText.textContent = 'Testing...';

  if (testBox) {
    testBox.style.display = 'flex';
    testBox.className = 'test-result-box test-pending';
  }
  if (testBadge) testBadge.textContent = 'Testing Connection...';
  if (testLatency) testLatency.textContent = '';
  if (testMessage) testMessage.textContent = `Connecting to ${payload.url}...`;
  if (testDetails) testDetails.style.display = 'none';

  try {
    const res = await fetch('/api/v1/connections/test', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    const data = await res.json();
    const isSuccess = data.success === true;
    const latency = data.latencyMs !== undefined ? `${data.latencyMs}ms` : '';

    if (testBox) {
      testBox.className = isSuccess
        ? 'test-result-box test-success'
        : 'test-result-box test-failed';
    }
    if (testBadge) {
      testBadge.textContent = isSuccess
        ? `✓ ${data.statusCode || 200} Connected`
        : `✕ Failed (${data.statusCode || 'Error'})`;
    }
    if (testLatency) testLatency.textContent = latency;
    if (testMessage) testMessage.textContent = data.message || (isSuccess ? 'Success' : 'Connection failed');

    if (data.details && Object.keys(data.details).length > 0) {
      if (testDetails) {
        testDetails.textContent = JSON.stringify(data.details, null, 2);
        testDetails.style.display = 'block';
      }
    }

    showToast(
      isSuccess ? `Connection verified successfully (${latency})` : `Test failed: ${data.message}`,
      isSuccess ? 'success' : 'error'
    );
  } catch (err: any) {
    if (testBox) testBox.className = 'test-result-box test-failed';
    if (testBadge) testBadge.textContent = '✕ Request Error';
    if (testMessage) testMessage.textContent = err.message;
    showToast(`Test error: ${err.message}`, 'error');
  } finally {
    if (testBtn) testBtn.disabled = false;
    if (testBtnText) testBtnText.textContent = 'Test Connection';
  }
}

/**
 * Test a stored connection by ID
 */
export async function testConnectionById(id: string, buttonEl?: HTMLElement | null): Promise<void> {
  const btn = buttonEl as HTMLButtonElement | null;
  if (btn) {
    btn.disabled = true;
    btn.innerHTML = '<span>Testing...</span>';
  }

  try {
    const res = await fetch(`/api/v1/connections/${encodeURIComponent(id)}/test`, {
      method: 'POST',
    });

    const data = await res.json();
    const isSuccess = data.success === true;

    state.connections.testResults[id] = {
      success: isSuccess,
      statusCode: data.statusCode || (isSuccess ? 200 : 500),
      latencyMs: data.latencyMs || 0,
      message: data.message || '',
    };

    showToast(
      isSuccess
        ? `Connection "${id}" verified (${data.latencyMs}ms)`
        : `Connection "${id}" failed: ${data.message}`,
      isSuccess ? 'success' : 'error'
    );

    renderActivePair(state.connections.source, state.connections.target);
    renderConnectionsList();
  } catch (err: any) {
    showToast(`Failed to test connection ${id}: ${err.message}`, 'error');
  } finally {
    if (btn) {
      btn.disabled = false;
      btn.innerHTML = '<span>Test Link</span>';
    }
  }
}

/**
 * Save / Update Connection handler
 */
export async function handleSaveConnection(event: Event): Promise<void> {
  event.preventDefault();

  const saveBtn = document.getElementById('btn-save-conn') as HTMLButtonElement | null;
  const saveBtnText = document.getElementById('save-btn-text');

  let payload: Record<string, any>;
  try {
    payload = collectFormPayload();
  } catch (err: any) {
    showToast(err.message, 'error');
    return;
  }

  const isEditing = Boolean(state.connections.editingId);
  const url = isEditing
    ? `/api/v1/connections/${encodeURIComponent(state.connections.editingId!)}`
    : '/api/v1/connections';
  const method = isEditing ? 'PUT' : 'POST';

  if (saveBtn) saveBtn.disabled = true;
  if (saveBtnText) saveBtnText.textContent = 'Saving...';

  try {
    const res = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.message || `HTTP ${res.status}`);
    }

    showToast(
      isEditing
        ? `Connection "${data.connection?.name || payload.name}" updated successfully`
        : `Connection "${data.connection?.name || payload.name}" created successfully`,
      'success'
    );

    resetConnectionForm();
    await fetchConnections();
  } catch (err: any) {
    showToast(`Failed to save connection: ${err.message}`, 'error');
  } finally {
    if (saveBtn) saveBtn.disabled = false;
    if (saveBtnText) {
      saveBtnText.textContent = isEditing ? 'Update Connection' : 'Save Connection';
    }
  }
}

/**
 * Activate a connection as active source or target
 */
export async function activateConnection(id: string): Promise<void> {
  try {
    const res = await fetch(`/api/v1/connections/${encodeURIComponent(id)}/activate`, {
      method: 'POST',
    });

    const data = await res.json();
    if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`);

    showToast(data.message || `Connection activated`, 'success');
    await fetchConnections();
  } catch (err: any) {
    showToast(`Activation failed: ${err.message}`, 'error');
  }
}

/**
 * Modal Handling for Delete
 */
export function openDeleteModal(id: string, name?: string): void {
  state.connections.pendingDeleteId = id;
  const modal = document.getElementById('delete-modal');
  const nameDisplay = document.getElementById('delete-conn-name-display');

  if (nameDisplay) nameDisplay.textContent = `"${name || id}"`;
  if (modal) modal.style.display = 'flex';
}

export function closeDeleteModal(): void {
  state.connections.pendingDeleteId = null;
  const modal = document.getElementById('delete-modal');
  if (modal) modal.style.display = 'none';
}

export async function confirmDeleteConnection(): Promise<void> {
  const id = state.connections.pendingDeleteId;
  if (!id) return;

  const btnConfirm = document.getElementById('btn-confirm-delete') as HTMLButtonElement | null;
  if (btnConfirm) btnConfirm.disabled = true;

  try {
    const res = await fetch(`/api/v1/connections/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });

    const data = await res.json();
    if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`);

    showToast(`Connection deleted successfully`, 'success');
    closeDeleteModal();
    if (state.connections.editingId === id) {
      resetConnectionForm();
    }
    await fetchConnections();
  } catch (err: any) {
    showToast(`Delete failed: ${err.message}`, 'error');
  } finally {
    if (btnConfirm) btnConfirm.disabled = false;
  }
}

/**
 * Modal Handling for OAuth Flow
 */
export function openOAuthModal(): void {
  const modal = document.getElementById('oauth-modal');
  if (modal) modal.style.display = 'flex';
}

export function closeOAuthModal(): void {
  const modal = document.getElementById('oauth-modal');
  if (modal) modal.style.display = 'none';
}

export function startOAuthFlow(): void {
  const pimUrlInput = document.getElementById('oauth-pim-url') as HTMLInputElement | null;
  const roleRadioTarget = document.getElementById('oauth-role-target') as HTMLInputElement | null;
  const clientIdInput = document.getElementById('oauth-client-id') as HTMLInputElement | null;

  let rawUrl = (pimUrlInput?.value || '').trim();
  if (!rawUrl) {
    showToast('Please enter an Akeneo PIM URL', 'error');
    return;
  }

  if (!rawUrl.startsWith('http://') && !rawUrl.startsWith('https://')) {
    rawUrl = `https://${rawUrl}`;
  }

  const role = roleRadioTarget?.checked ? 'target' : 'source';
  const customCid = (clientIdInput?.value || '').trim();

  let connectUrl = `/oauth/connect?pim_url=${encodeURIComponent(rawUrl)}&connection_type=${encodeURIComponent(role)}&redirect=true`;
  if (customCid) {
    connectUrl += `&client_id=${encodeURIComponent(customCid)}`;
  }

  closeOAuthModal();
  showToast('Redirecting to Akeneo OAuth authorization...', 'info');
  window.location.href = connectUrl;
}

/**
 * Scroll smoothly to connection form
 */
export function scrollToForm(): void {
  resetConnectionForm();
  const formCard = document.getElementById('connection-form-card');
  if (formCard) {
    formCard.scrollIntoView({ behavior: 'smooth', block: 'start' });
    const nameInput = document.getElementById('conn-name') as HTMLInputElement | null;
    if (nameInput) nameInput.focus();
  }
}
